import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import { getUser } from '../../utils/auth';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Skeleton } from '../../components/ui/skeleton';
import { Avatar, AvatarFallback } from '../../components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import {
  Search,
  Clock,
  MapPin,
  FileText,
  MessageSquare,
  DollarSign,
  Send,
  Eye,
  AlertCircle,
  CheckCircle2
} from 'lucide-react';

const URGENCY_LABELS = { urgent: 'High', high: 'High', soon: 'Medium', medium: 'Medium', routine: 'Low', low: 'Low' };

export default function DoctorInquiries() {
  const [loading, setLoading] = useState(true);
  const [inquiries, setInquiries] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [quoteDialogOpen, setQuoteDialogOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [quoteData, setQuoteData] = useState({
    treatmentCost: '',
    hospitalStay: '',
    estimatedDays: '',
    additionalNotes: ''
  });

  const loadInquiries = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/doctor/treatment-requests');
      setInquiries((data.requests || []).map((r) => ({
        id: r._id,
        patient: r.patient?.display_name || 'Patient',
        country: r.patient?.country || '—',
        condition: r.condition || r.preferred_specialization || 'Treatment request',
        symptoms: r.symptoms || r.additional_notes || '',
        urgency: URGENCY_LABELS[r.urgency] || 'Medium',
        time: r.created_at ? new Date(r.created_at).toLocaleDateString() : '',
        quotesReceived: r.quotes_count || 0,
      })));
    } catch (error) {
      toast.error('Failed to load inquiries');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadInquiries();
  }, [loadInquiries]);

  const getUrgencyColor = (urgency) => {
    switch (urgency) {
      case 'High': return 'bg-red-100 text-red-700 border-red-200';
      case 'Medium': return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'Low': return 'bg-green-100 text-green-700 border-green-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const handleSendQuote = async () => {
    if (!quoteData.treatmentCost || !quoteData.estimatedDays) {
      toast.error('Please fill in required fields');
      return;
    }
    const cost = Number(quoteData.treatmentCost);
    try {
      await api.post('/doctor/quotes', {
        treatment_request_id: selectedInquiry.id,
        doctor_id: getUser()?._id || '',
        treatment_plan: quoteData.additionalNotes || selectedInquiry.condition,
        consultation_fee: 0,
        procedure_cost: cost,
        total_cost: cost,
        currency: 'INR',
        estimated_duration: `${quoteData.estimatedDays} days`,
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not send the quote');
      return;
    }
    toast.success('Quote sent successfully!');
    loadInquiries();
    setQuoteDialogOpen(false);
    setSelectedInquiry(null);
    setQuoteData({ treatmentCost: '', hospitalStay: '', estimatedDays: '', additionalNotes: '' });
  };

  const filteredInquiries = inquiries.filter(inquiry =>
    inquiry.patient.toLowerCase().includes(searchTerm.toLowerCase()) ||
    inquiry.condition.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="doctor-inquiries">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Patient Inquiries</h1>
          <p className="text-gray-500 text-sm mt-1">{inquiries.length} pending inquiries</p>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input
          data-testid="inquiry-search-input"
          placeholder="Search inquiries..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Inquiries Grid */}
      {loading ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-64" />)}
        </div>
      ) : filteredInquiries.length === 0 ? (
        <Card className="border shadow-sm">
          <CardContent className="p-12 text-center">
            <MessageSquare className="w-12 h-12 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500">No inquiries found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {filteredInquiries.map((inquiry) => (
            <Card key={inquiry.id} className="border shadow-sm hover:shadow-md transition-shadow">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <Avatar className="h-12 w-12">
                      <AvatarFallback className="bg-teal-100 text-teal-700">
                        {inquiry.patient.replace('Patient #', '').slice(0, 2).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <CardTitle className="text-lg">{inquiry.patient}</CardTitle>
                      <p className="text-sm text-gray-500 flex items-center gap-1">
                        <MapPin className="w-3 h-3" /> {inquiry.country}
                      </p>
                    </div>
                  </div>
                  <Badge className={`${getUrgencyColor(inquiry.urgency)} border`}>
                    {inquiry.urgency} Priority
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="font-semibold text-gray-900">{inquiry.condition}</p>
                  <p className="text-sm text-gray-600 mt-1 line-clamp-2">{inquiry.symptoms}</p>
                </div>
                
                <div className="flex items-center gap-4 text-sm text-gray-500">
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" /> {inquiry.time}
                  </span>
                  <span className="flex items-center gap-1">
                    <FileText className="w-4 h-4" /> {inquiry.quotesReceived} quotes
                  </span>
                </div>

                <div className="flex gap-2">
                  <Button
                    className="flex-1 bg-teal-600 hover:bg-teal-700"
                    data-testid={`send-quote-btn-${inquiry.id}`}
                    onClick={() => {
                      setSelectedInquiry(inquiry);
                      setQuoteDialogOpen(true);
                    }}
                  >
                    <Send className="w-4 h-4 mr-2" /> Send Quote
                  </Button>
                  <Button variant="outline" data-testid={`inquiry-details-btn-${inquiry.id}`} onClick={() => { setSelectedInquiry(inquiry); setDetailsOpen(true); }}>
                    <Eye className="w-4 h-4 mr-2" /> Details
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="max-w-lg" data-testid="inquiry-details-dialog">
          <DialogHeader>
            <DialogTitle>{selectedInquiry?.patient}</DialogTitle>
            <DialogDescription>{selectedInquiry?.condition} · {selectedInquiry?.country}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p><span className="font-medium">Urgency:</span> {selectedInquiry?.urgency}</p>
            <p><span className="font-medium">Received:</span> {selectedInquiry?.time}</p>
            <p className="text-gray-700 whitespace-pre-line">{selectedInquiry?.symptoms || 'No symptoms provided.'}</p>
            <p className="text-xs text-gray-500">Patient contact details stay private until the patient accepts a quote.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailsOpen(false)}>Close</Button>
            <Button className="bg-teal-600 hover:bg-teal-700" data-testid="details-send-quote-btn" onClick={() => { setDetailsOpen(false); setQuoteDialogOpen(true); }}>
              <Send className="w-4 h-4 mr-2" /> Send Quote
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Quote Dialog */}
      <Dialog open={quoteDialogOpen} onOpenChange={setQuoteDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Send Quote to {selectedInquiry?.patient}</DialogTitle>
            <DialogDescription>Provide treatment cost estimate for {selectedInquiry?.condition}</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Treatment Cost (INR) *</Label>
                <Input
                  type="number"
                  data-testid="quote-cost-input"
                  placeholder="e.g., 500000"
                  value={quoteData.treatmentCost}
                  onChange={(e) => setQuoteData({...quoteData, treatmentCost: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Estimated Days *</Label>
                <Input
                  type="number"
                  data-testid="quote-days-input"
                  placeholder="e.g., 7"
                  value={quoteData.estimatedDays}
                  onChange={(e) => setQuoteData({...quoteData, estimatedDays: e.target.value})}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Hospital Stay (days)</Label>
              <Input
                type="number"
                placeholder="e.g., 5"
                value={quoteData.hospitalStay}
                onChange={(e) => setQuoteData({...quoteData, hospitalStay: e.target.value})}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Additional Notes</Label>
              <Textarea
                placeholder="Include any special instructions, pre-operative requirements, etc."
                value={quoteData.additionalNotes}
                onChange={(e) => setQuoteData({...quoteData, additionalNotes: e.target.value})}
                rows={4}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setQuoteDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSendQuote} data-testid="quote-submit-btn" className="bg-teal-600 hover:bg-teal-700">
              <Send className="w-4 h-4 mr-2" /> Send Quote
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
